import { useState, useRef, useEffect, useCallback } from "react";
import axios from "../axios";
import * as url from "../utils/url";
import {
  TRANSLATE_ERROR_GENERIC,
  TRANSLATE_PASS_WINDOW,
  TRANSLATE_REQUEST_TIMEOUT_MS,
  TRANSLATE_STATUS_DONE,
  TRANSLATE_STATUS_ERROR,
  TRANSLATE_STATUS_IDLE,
  TRANSLATE_STATUS_RUNNING,
  createEnglishCache,
  mergeShared,
  pickOptionText,
  pickQuestionText,
} from "../utils/translateResponsesCore";

// A very large Trivvy may need a few asks before every string has its English.
const MAX_FOLLOW_UPS = 3;
const FOLLOW_UP_WAIT_MS = 3000;

// Results "By Question" English: only the Trivvy's own copy (question titles and Multiple Choice /
// Check Boxes options, by option id). Nothing is requested until the admin first picks English, so
// a page that stays on Original costs nothing. Picking English asks the BE to process the shared
// copy (a window with no rows: no employee answers), then reads the cache; a lasting failure keeps
// the originals and is tried again the next time English is picked (or on Retry).
// status: idle | running (shown as "Translating questions and options...") | done | error.
const useTranslatedContent = ({ triviaId, active }) => {
  const [version, setVersion] = useState(0);
  const [status, setStatus] = useState(TRANSLATE_STATUS_IDLE);
  const [error, setError] = useState(null);
  const cacheRef = useRef(createEnglishCache());
  const runRef = useRef(0);
  const timerRef = useRef(null);
  const needsRetryRef = useRef(false);

  const stop = useCallback(() => {
    runRef.current += 1;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const load = useCallback(() => {
    stop();
    if (!triviaId) {
      return;
    }
    const runId = runRef.current;
    const isCurrent = () => runId === runRef.current;
    setStatus(TRANSLATE_STATUS_RUNNING);
    setError(null);
    const read = () =>
      axios
        .get(url.getContentTranslations(triviaId), { timeout: TRANSLATE_REQUEST_TIMEOUT_MS })
        .then((res) => (res && res.data ? res.data.data : null));
    const ask = (followUps) => {
      axios
        .post(
          url.processResponseTranslations(triviaId),
          { mode: TRANSLATE_PASS_WINDOW, resultIds: [] },
          { timeout: TRANSLATE_REQUEST_TIMEOUT_MS }
        )
        // Processing may be switched off or busy: the cached copy is still read.
        .catch(() => null)
        .then(read)
        .then((data) => {
          if (!isCurrent()) {
            return;
          }
          if (data && mergeShared(cacheRef.current, data.shared) > 0) {
            setVersion((v) => v + 1);
          }
          if (!cacheRef.current.sharedComplete) {
            if (followUps < MAX_FOLLOW_UPS) {
              timerRef.current = setTimeout(() => ask(followUps + 1), FOLLOW_UP_WAIT_MS);
              return;
            }
            needsRetryRef.current = true;
            setStatus(TRANSLATE_STATUS_ERROR);
            setError({ kind: TRANSLATE_ERROR_GENERIC });
            return;
          }
          needsRetryRef.current = false;
          setStatus(TRANSLATE_STATUS_DONE);
        })
        .catch(() => {
          // The original copy stays on screen; picking English (or Retry) asks again.
          if (isCurrent()) {
            needsRetryRef.current = true;
            setStatus(TRANSLATE_STATUS_ERROR);
            setError({ kind: TRANSLATE_ERROR_GENERIC });
          }
        });
    };
    needsRetryRef.current = false;
    ask(0);
  }, [triviaId, stop]);

  // A different Trivvy starts clean.
  const requestedRef = useRef(false);
  useEffect(() => {
    cacheRef.current = createEnglishCache();
    setVersion((v) => v + 1);
    setStatus(TRANSLATE_STATUS_IDLE);
    setError(null);
    needsRetryRef.current = false;
    requestedRef.current = false;
    return stop;
  }, [triviaId, stop]);

  // The first switch to English asks; only a later switch asks again, so a lasting failure never
  // loops. Switching back to Original stops follow-ups.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    const turnedOn = active && !wasActiveRef.current;
    wasActiveRef.current = active;
    if (!active || !triviaId) {
      stop();
      setStatus(TRANSLATE_STATUS_IDLE);
      setError(null);
      // A run stopped before the copy was complete is asked again next time.
      if (requestedRef.current && !cacheRef.current.sharedComplete) {
        needsRetryRef.current = true;
      }
      return;
    }
    if (!requestedRef.current || (turnedOn && needsRetryRef.current)) {
      requestedRef.current = true;
      load();
    }
  }, [active, triviaId, load, stop]);

  const getQuestionText = useCallback(
    (questionId, original) => (active ? pickQuestionText(cacheRef.current, questionId, original) : original),
    [active, version]
  );

  // optionId: the option's stable id; only the Trivvy's current options are passed here.
  const getOptionText = useCallback(
    (questionId, optionId, original) =>
      active ? pickOptionText(cacheRef.current, questionId, optionId, original) : original,
    [active, version]
  );

  const retry = useCallback(() => {
    if (active) {
      load();
    }
  }, [active, load]);

  return {
    getQuestionText: getQuestionText,
    getOptionText: getOptionText,
    status: status,
    error: error,
    retry: retry,
  };
};

export default useTranslatedContent;
